// src/components/faq/FaqList.tsx
import React from 'react';
import {FaqAccordionItem} from './FaqAccordionItem';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchX } from 'lucide-react';
import {Button} from '@dopomogai/ui';

interface FaqItem {
  q: string;
  a: string;
}

interface NoResultsData {
  headline: string;
  text: string;
  button: string;
}

interface FaqListProps {
  items: FaqItem[];
  noResultsData: NoResultsData;
  searchQuery: string;
  onClearSearch: () => void;
}

export const FaqList: React.FC<FaqListProps> = ({ items, noResultsData, searchQuery, onClearSearch }) => {

  const listContainerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const listItemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="bg-white py-12 min-h-[40vh]">
      <div className="container mx-auto px-4 max-w-4xl">
        <AnimatePresence mode="wait">
          {items.length > 0 ? (
            <motion.div
              key="faq-list"
              variants={listContainerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
            >
              {items.map((item, index) => (
                <motion.div key={item.q + index} variants={listItemVariants}>
                  <FaqAccordionItem question={item.q} answer={item.a} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="no-results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center py-16 px-6"
            >
              <SearchX size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-2xl font-bold text-slate-800">
                {noResultsData.headline}
              </h3>
              <p className="text-slate-500 mt-2 mb-6">
                {noResultsData.text.replace('{query}', `"${searchQuery}"`)}
              </p>
              <Button
                text={noResultsData.button}
                variant="secondary"
                onClick={onClearSearch}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

